import { CommodityAttribute, ContainerHeight, ContainerType } from '@planning/app/api'
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import { DefaultParamType, TFnType, TranslationKey } from '@tolgee/react'
import { getContainerAttributesParameters } from '@tom-ui/ui'
import React, { ReactElement } from 'react'

export interface RoutingSlipPdfProps {
  dropOffs?: RoutingSlipItem[]
  pickUps?: RoutingSlipItem[]
  miniMap?: ReactElement | null
  t: TFnType<DefaultParamType, string, TranslationKey>
}

export interface NumericContainerInfo {
  type: 'numeric'
  containerNumber: string
  location: string
}

export interface GeneralCargoInfo {
  type: 'generalCargo'
  referenceNumber?: string
  commodityName?: string
  commodityAttributes?: CommodityAttribute[] | null
  plannedCargoAmount?: number | null
  location: string
}

export interface NonNumericContainerInfo {
  type: 'non-numeric'
  location: string
  archetype: string
  length: number[]
  height: ContainerHeight[]
  containerType: ContainerType[]
  operator?: string
  referenceNumber?: string
  suggestedContainers?: string[]
}

export type RoutingSlipItem = NumericContainerInfo | GeneralCargoInfo | NonNumericContainerInfo

const styles = StyleSheet.create({
  page: { padding: 20, fontSize: 12 },
  pageTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  section: {
    border: '1pt solid #e0e0e0',
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
  },
  header: {
    padding: 10,
    borderBottom: '1pt solid #e0e0e0',
    backgroundColor: '#ffffff',
  },
  headerText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  containerItem: {
    backgroundColor: '#f9f9f9',
    padding: 10,
    borderBottom: '1pt solid #e0e0e0',
  },
  lastContainerItem: {
    borderBottom: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  containerNumber: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  icon: {
    marginRight: 4,
    fontSize: 10,
  },
  locationText: {
    fontSize: 11,
  },
  label: {
    fontWeight: 'bold',
    fontSize: 11,
    marginRight: 4,
  },
})

export const RoutingSlip: React.FC<RoutingSlipPdfProps> = ({ dropOffs, pickUps, miniMap, t }) => {
  const { heights: containerHeightParameters, types: containerTypeParameters } =
    getContainerAttributesParameters(t)

  const formatCommodityAttributes = (attributes?: CommodityAttribute[] | null): string =>
    (attributes ?? [])
      .filter(attribute => attribute?.name && attribute?.value)
      .map(attribute => {
        const name = attribute.name
        return `${name?.charAt(0).toUpperCase()}${name?.slice(1)}: ${attribute.value}`
      })
      .join(', ')

  const renderContainerItem = (item: RoutingSlipItem, isLast: boolean, key: number) => {
    const location = item.location || t('noLocationProvided', 'No location provided')
    const commodityAttributes =
      item.type === 'generalCargo' ? formatCommodityAttributes(item.commodityAttributes) : ''

    return (
      <View
        key={key}
        style={isLast ? [styles.containerItem, styles.lastContainerItem] : styles.containerItem}
      >
        {item.type === 'numeric' ? (
          <>
            <Text style={styles.containerNumber}>{item.containerNumber}</Text>
            <Text style={styles.locationText}>{location}</Text>
          </>
        ) : item.type === 'generalCargo' ? (
          <>
            <Text style={styles.containerNumber}>
              {t('generalCargoOrder', 'General cargo order')}
            </Text>
            {item.referenceNumber && (
              <Text style={styles.locationText}>
                <Text style={styles.label}>{t('referenceNumber', 'Reference number')}:</Text>
                {item.referenceNumber}
              </Text>
            )}
            {item.commodityName && (
              <Text style={styles.locationText}>
                <Text style={styles.label}>{t('commodity', 'Commodity')}:</Text>
                {item.commodityName}
              </Text>
            )}
            {item.plannedCargoAmount !== null && item.plannedCargoAmount !== undefined && (
              <Text style={styles.locationText}>
                <Text style={styles.label}>{t('planned', 'Planned')}:</Text>
                {item.plannedCargoAmount}
              </Text>
            )}
            {commodityAttributes && (
              <Text style={styles.locationText}>
                <Text style={styles.label}>
                  {t('commodityAttributes', 'Commodity attributes')}:
                </Text>
                {commodityAttributes}
              </Text>
            )}
            <Text style={styles.locationText}>{location}</Text>
          </>
        ) : (
          <>
            <Text style={styles.containerNumber}>
              {t('nonNumericContainer', 'Non-numeric container')} {item.referenceNumber}
            </Text>
            {item.suggestedContainers && item.suggestedContainers.length > 0 && (
              <Text style={styles.locationText}>
                <Text style={styles.label}>
                  {t('suggestedContainers', 'Suggested containers')}:
                </Text>
                {item.suggestedContainers?.join(', ')}
              </Text>
            )}
            <Text style={{ ...styles.row }}>{location}</Text>
            <View style={{ ...styles.row, marginTop: 6 }}>
              <Text style={styles.label}>{t('operator', 'Operator')}:</Text>
              <Text>{item.operator}</Text>
            </View>
            <View style={{ ...styles.row, marginTop: 6 }}>
              <Text style={styles.label}>{t('archetype', 'Archetype')}:</Text>
              <Text>{item.archetype}</Text>
            </View>
            <View style={{ ...styles.row }}>
              <Text style={styles.label}>{t('lengths', 'Lengths')}:</Text>
              <Text>{item.length.join(', ')}</Text>
              <Text style={styles.label}>{t('heights', 'Heights')}:</Text>
              <Text>
                {item.height?.map(h => containerHeightParameters[h]?.label)?.join(' ') ?? ''}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>{t('types', 'Types')}:</Text>
              <Text style={{ width: '75%' }}>
                {item.containerType
                  ?.map(t => containerTypeParameters[t]?.label)
                  .filter(Boolean)
                  .map(x => x.trim())
                  .join(' ')}
              </Text>
            </View>
          </>
        )}
      </View>
    )
  }

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <Text style={styles.pageTitle}>{t('routingSlip', 'Routing slip')}</Text>

        {miniMap && <View style={{ marginBottom: 12 }}>{miniMap}</View>}

        <View style={styles.section}>
          <View style={styles.header}>
            <Text style={styles.headerText}>{t('dropOffs', 'Drop offs')}</Text>
          </View>
          {dropOffs?.map((item, index) =>
            renderContainerItem(item, index === dropOffs.length - 1, index),
          )}
        </View>

        <View style={styles.section}>
          <View style={styles.header}>
            <Text style={styles.headerText}>{t('pickUps', 'Pick ups')}</Text>
          </View>
          {pickUps?.map((item, index) =>
            renderContainerItem(item, index === pickUps.length - 1, index),
          )}
        </View>
      </Page>
    </Document>
  )
}
